import { useState } from 'react';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Tabs, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDate, formatNumber, ORDER_STATUS_LABELS } from '../lib/format';
import { IconCart } from '../components/icons';

export function ProcurementPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [tab, setTab] = useState<'bestellungen' | 'lieferanten'>('bestellungen');
  const [detail, setDetail] = useState<number | null>(null);
  const [newOrder, setNewOrder] = useState(false);
  const [rejecting, setRejecting] = useState<any | null>(null);

  const orders = useAsync(() => api.get('/api/procurement/orders'), [tab]);
  const suppliers = useAsync(() => api.get('/api/procurement/suppliers'), []);

  const act = async (id: number, action: string, body?: unknown) => {
    try {
      await api.post(`/api/procurement/orders/${id}/${action}`, body);
      notify('Bestellung aktualisiert.', 'success');
      orders.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion fehlgeschlagen.', 'error');
    }
  };

  const pending = (orders.data?.orders ?? []).filter((order: any) => order.status === 'pending_approval');

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconCart width={22} height={22} />}
        title="Materialanforderungen"
        subtitle={
          <>
            Bestellanforderungen, Freigabeworkflow ab{' '}
            {formatCurrency(orders.data?.approvalThreshold ?? 250)} Bestellwert, Übermittlung an Lieferanten und
            Wareneingang mit Bestandsbuchung.
          </>
        }
        pill="Materialanforderungen"
        tone="warning"
        actions={
          can('procurement.request') && (
            <button className="btn primary" onClick={() => setNewOrder(true)}>
              Bestellanforderung
            </button>
          )
        }
      />

      {pending.length > 0 && can('procurement.approve') && (
        <div className="alert warning">
          <strong>{pending.length} Bestellung(en)</strong> warten auf Ihre Freigabe.
        </div>
      )}

      <Card noBody>
        <div className="card-body tight" style={{ paddingBottom: 0 }}>
          <Tabs
            active={tab}
            onChange={setTab}
            tabs={[
              { key: 'bestellungen', label: 'Bestellungen', count: orders.data?.orders?.length },
              { key: 'lieferanten', label: 'Lieferanten', count: suppliers.data?.length },
            ]}
          />
        </div>

        {tab === 'bestellungen' &&
          (orders.loading ? (
            <Loading />
          ) : !orders.data?.orders?.length ? (
            <EmptyState title="Keine Bestellungen" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Nummer</th>
                    <th>Lieferant</th>
                    <th>Status</th>
                    <th className="num">Positionen</th>
                    <th className="num">Netto</th>
                    <th>Angefordert von</th>
                    <th>Liefertermin</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {orders.data.orders.map((order: any) => (
                    <tr key={order.id}>
                      <td className="mono clickable" onClick={() => setDetail(order.id)}>
                        {order.order_no}
                      </td>
                      <td className="small">{order.supplier_name ?? '–'}</td>
                      <td>
                        <Badge variant={orderVariant(order.status)}>{ORDER_STATUS_LABELS[order.status] ?? order.status}</Badge>
                      </td>
                      <td className="num">{order.item_count}</td>
                      <td className="num">{formatCurrency(order.total_net)}</td>
                      <td className="small">
                        {order.requester_first_name ? `${order.requester_first_name} ${order.requester_last_name}` : '–'}
                      </td>
                      <td className="small">{order.expected_at ? formatDate(order.expected_at) : '–'}</td>
                      <td className="actions">
                        {order.status === 'pending_approval' && can('procurement.approve') && (
                          <>
                            <button className="btn primary sm" onClick={() => act(order.id, 'approve')}>
                              Freigeben
                            </button>{' '}
                            <button className="btn sm" onClick={() => setRejecting(order)}>
                              Ablehnen
                            </button>
                          </>
                        )}
                        {order.status === 'approved' && can('procurement.order') && (
                          <button className="btn sm" onClick={() => act(order.id, 'send')}>
                            Bestellen
                          </button>
                        )}
                        {['ordered', 'partially_received'].includes(order.status) && can('procurement.receive') && (
                          <button className="btn sm" onClick={() => setDetail(order.id)}>
                            Wareneingang
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

        {tab === 'lieferanten' &&
          (suppliers.loading ? (
            <Loading />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Lieferant</th>
                    <th>Ansprechpartner</th>
                    <th>Bestellweg</th>
                    <th className="num">Lieferzeit</th>
                    <th className="num">Artikel</th>
                  </tr>
                </thead>
                <tbody>
                  {(suppliers.data ?? []).map((supplier: any) => (
                    <tr key={supplier.id}>
                      <td>
                        <strong>{supplier.name}</strong>
                        <div className="small subtle">{supplier.email}</div>
                      </td>
                      <td className="small">{supplier.contact_name ?? '–'}</td>
                      <td>
                        <Badge variant="info">{supplier.order_method.toUpperCase()}</Badge>
                      </td>
                      <td className="num small">{supplier.lead_days} Tage</td>
                      <td className="num">{supplier.article_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Card>

      {detail && <OrderDetailModal orderId={detail} onClose={() => setDetail(null)} onChanged={() => orders.reload()} />}
      {newOrder && (
        <NewOrderModal
          onClose={() => setNewOrder(false)}
          onSaved={() => {
            setNewOrder(false);
            orders.reload();
            notify('Bestellanforderung erstellt.', 'success');
          }}
        />
      )}
      {rejecting && (
        <RejectOrderModal
          order={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            orders.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Ablehnung mit Begründung.
 *
 * Vorher stand hier `window.prompt`. Bricht der Anwender ab – oder unterdrückt
 * der Browser den Dialog, was auf Mobilgeräten der Normalfall ist –, kommt
 * `null` zurück, und die Ablehnung wurde stillschweigend verworfen: Der Knopf
 * schien wirkungslos. Ein eigenes Fenster kann nicht unterdrückt werden und
 * zeigt außerdem, warum die Eingabe abgelehnt wird.
 */
function RejectOrderModal({ order, onClose, onDone }: { order: any; onClose: () => void; onDone: () => void }) {
  const { notify } = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/procurement/orders/${order.id}/reject`, { reason });
      notify(`Bestellung ${order.order_no} abgelehnt.`, 'success');
      onDone();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Bestellung ablehnen – ${order.order_no}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn danger" disabled={busy || reason.trim().length < 3} onClick={submit}>
            {busy ? 'Wird abgelehnt …' : 'Ablehnen'}
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field
          label="Begründung"
          required
          hint="Sie geht an die anfordernde Person und wird am verknüpften Auftrag vermerkt."
        >
          <textarea rows={3} value={reason} autoFocus onChange={(event) => setReason(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function OrderDetailModal({ orderId, onClose, onChanged }: { orderId: number; onClose: () => void; onChanged: () => void }) {
  const { can } = useAuth();
  const order = useAsync(() => api.get(`/api/procurement/orders/${orderId}`), [orderId]);
  const warehouses = useAsync(() => api.get('/api/material/warehouses'), []);
  const [warehouseId, setWarehouseId] = useState('');
  const [received, setReceived] = useState<Record<number, string>>({});
  const [error, setError] = useState<unknown>(null);

  const canReceive = can('procurement.receive') && ['ordered', 'partially_received'].includes(order.data?.status);

  return (
    <Modal
      title={`Bestellung ${order.data?.order_no ?? ''}`}
      onClose={onClose}
      size="wide"
      footer={
        canReceive ? (
          <>
            <button className="btn" onClick={onClose}>
              Schließen
            </button>
            <button
              className="btn primary"
              disabled={!warehouseId}
              onClick={async () => {
                try {
                  await api.post(`/api/procurement/orders/${orderId}/receive`, {
                    warehouseId: Number(warehouseId),
                    positions: Object.entries(received)
                      .filter(([, value]) => Number(value) > 0)
                      .map(([itemId, value]) => ({ itemId: Number(itemId), quantity: Number(value) })),
                  });
                  onChanged();
                  onClose();
                } catch (caught) {
                  setError(caught);
                }
              }}
            >
              Wareneingang buchen
            </button>
          </>
        ) : (
          <button className="btn" onClick={onClose}>
            Schließen
          </button>
        )
      }
    >
      {order.loading ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorMessage error={error} />
          <dl className="definition-list">
            <dt>Status</dt>
            <dd>
              <Badge variant={orderVariant(order.data.status)}>
                {ORDER_STATUS_LABELS[order.data.status] ?? order.data.status}
              </Badge>
            </dd>
            <dt>Lieferant</dt>
            <dd>{order.data.supplier_name ?? '–'}</dd>
            <dt>Bestellwert</dt>
            <dd>{formatCurrency(order.data.total_net)}</dd>
            <dt>Bemerkung</dt>
            <dd className="small">{order.data.note ?? '–'}</dd>
          </dl>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th className="num">Bestellt</th>
                  <th className="num">Geliefert</th>
                  <th className="num">Preis</th>
                  {canReceive && <th className="num">Jetzt eingehen</th>}
                </tr>
              </thead>
              <tbody>
                {(order.data.items ?? []).map((item: any) => (
                  <tr key={item.id}>
                    <td>
                      {item.article_name}
                      <div className="small subtle mono">{item.article_no}</div>
                    </td>
                    <td className="num">
                      {formatNumber(item.quantity, 2)} {item.unit}
                    </td>
                    <td className="num">{formatNumber(item.received_qty, 2)}</td>
                    <td className="num">{formatCurrency(item.unit_price)}</td>
                    {canReceive && (
                      <td className="num">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={received[item.id] ?? ''}
                          placeholder={String(item.quantity - item.received_qty)}
                          onChange={(event) => setReceived((current) => ({ ...current, [item.id]: event.target.value }))}
                          style={{ width: 90 }}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canReceive && (
            <Field label="Wareneingang auf Lagerort" required>
              <SearchSelect
                value={String(warehouseId ?? '')}
                onChange={(wert) => setWarehouseId(wert)}
                options={(warehouses.data ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.name) }))}
              />
            </Field>
          )}
        </div>
      )}
    </Modal>
  );
}

function NewOrderModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const suppliers = useAsync(() => api.get('/api/procurement/suppliers'), []);
  const articles = useAsync(() => api.get('/api/material/articles'), []);
  const [supplierId, setSupplierId] = useState('');
  const [note, setNote] = useState('');
  const [items, setItems] = useState<{ articleId: string; quantity: string }[]>([{ articleId: '', quantity: '1' }]);
  const [error, setError] = useState<unknown>(null);

  const total = items.reduce((sum, item) => {
    const article = (articles.data ?? []).find((entry: any) => String(entry.id) === item.articleId);
    return sum + (article ? article.purchase_price * Number(item.quantity || 0) : 0);
  }, 0);

  return (
    <Modal
      title="Bestellanforderung"
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!items.some((item) => item.articleId)}
            onClick={async () => {
              try {
                await api.post('/api/procurement/orders', {
                  supplierId: supplierId ? Number(supplierId) : null,
                  note: note || undefined,
                  items: items
                    .filter((item) => item.articleId)
                    .map((item) => ({ articleId: Number(item.articleId), quantity: Number(item.quantity) })),
                });
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Anfordern ({formatCurrency(total)})
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Lieferant">
          <SearchSelect
            value={String(supplierId ?? '')}
            onChange={(wert) => setSupplierId(wert)}
            options={(suppliers.data ?? []).map((supplier: any) => ({ value: String(supplier.id), label: String(supplier.name) }))}
          />
        </Field>

        <div className="stack" style={{ gap: 8 }}>
          <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>Positionen</label>
          {items.map((item, index) => (
            <div className="row" key={index} style={{ gap: 8, flexWrap: 'nowrap' }}>
              <SearchSelect
                value={String(item.articleId ?? '')}
                onChange={(wert) => setItems((current) => current.map((entry, i) => (i === index ? { ...entry, articleId: wert } : entry)))}
                options={(articles.data ?? []).map((article: any) => ({ value: String(article.id), label: `${article.article_no} · ${article.name}` }))}
                placeholder="Artikel wählen …"
                style={{ flex: 1 }}
              />
              <input
                type="number"
                min="1"
                value={item.quantity}
                onChange={(event) =>
                  setItems((current) => current.map((entry, i) => (i === index ? { ...entry, quantity: event.target.value } : entry)))
                }
                style={{ width: 100 }}
              />
              <button className="btn ghost sm" onClick={() => setItems((current) => current.filter((_, i) => i !== index))}>
                ×
              </button>
            </div>
          ))}
          <button className="btn sm" onClick={() => setItems((current) => [...current, { articleId: '', quantity: '1' }])}>
            Position hinzufügen
          </button>
        </div>

        <Field label="Bemerkung">
          <input value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function orderVariant(status: string): string {
  return (
    {
      draft: '',
      pending_approval: 'warning',
      approved: 'primary',
      ordered: 'info',
      partially_received: 'info',
      received: 'success',
      cancelled: 'danger',
    }[status] ?? ''
  );
}
